/** Google recommends the most specific GTIN property, so the value's length decides which is used. */
const GTIN_PROPERTY_BY_LENGTH: { [length: number]: string } = {
  12: "gtin12",
  13: "gtin13",
  14: "gtin14"
};

/** GS1 mod-10: reading right to left from the check digit, digits weigh 3, 1, 3, 1, … */
const hasValidCheckDigit = (gtin: string): boolean => {
  const digits = gtin.split("").map(Number);
  const checkDigit = digits[digits.length - 1];
  const sum = digits
    .slice(0, -1)
    .reverse()
    .reduce((total, digit, index) => total + digit * (index % 2 === 0 ? 3 : 1), 0);

  return (10 - (sum % 10)) % 10 === checkDigit;
};

/**
 * The EAN as its matching `gtinXX` property, or nothing when no usable value is present. A wrong
 * GTIN ties the page to someone else's product, so unsupported lengths and values failing the
 * check digit — both of which occur in the feed — are left out entirely.
 *
 * A product can carry several EANs separated by pipes: the same code both zero-padded and plain,
 * plus sibling variants. The shortest valid one wins, since a 13-digit EAN padded to 14 is formally
 * a GTIN-14, which describes cases and pallets rather than the item on the page.
 */
export const getGtinProperty = (ean?: string): { [property: string]: string } => {
  const candidates = (ean || "")
    .split("|")
    .map((value) => value.trim())
    .filter((value) => /^\d+$/.test(value) && !!GTIN_PROPERTY_BY_LENGTH[value.length] && hasValidCheckDigit(value))
    .sort((a, b) => a.length - b.length);

  const gtin = candidates[0];

  return gtin ? { [GTIN_PROPERTY_BY_LENGTH[gtin.length]]: gtin } : {};
};
